import React, { forwardRef } from 'react';
import { useNode } from '@craftjs/core';
import { PROP_POSICION, normalizarPosicion } from '../utils/posicionLibre';
import { htmlSeguro } from '../utils/htmlSeguro';
import { htmlConTipografiaMovil } from '../utils/tipografiaMovil';

export type TextAlignType = 'left' | 'center' | 'right' | 'justify';

interface TextProps {
  text?: string;
  fontSize?: number;
  fontWeight?: string;
  textAlign?: TextAlignType;
  color?: string;
  background?: string;
  padding?: number;
  margin?: number;
}

interface CraftComponent {
  craft: {
    displayName: string;
    props: Record<string, any>;
    related?: {
      toolbar?: React.ComponentType<any>;
    };
  };
}

/**
 * En un lienzo la caja del texto ES el frame.
 *
 * Espejo del editor: allá el bloque quedaba del tamaño de sus letras adentro de
 * un frame más alto y se veían dos cajas; acá hace falta lo mismo para que la
 * alineación vertical se calcule sobre TODO el alto y la página publicada se vea
 * igual que el canvas. La alineación viaja por `justify-content: inherit` desde
 * el envoltorio, que la fija según `_layout.v`.
 */
/** El bloque llena el frame y hereda de él la alineación vertical. */
const enElLienzo = (enLienzo: boolean): React.CSSProperties | null => (enLienzo
  ? {
    height: '100%',
    margin: 0,
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'inherit',
  }
  : null);

const TextComponent = forwardRef<HTMLDivElement, TextProps>(
  (
    {
      text = 'Edit me',
      fontSize = 16,
      fontWeight = 'normal',
      textAlign = 'left' as TextAlignType,
      color = '#000000',
      background = 'transparent',
      padding = 10,
      margin = 5,
    }: TextProps,
    ref
  ) => {
    const { connectors, selected, enLienzo } = useNode((state) => ({
      selected: state.events.selected,
      enLienzo: !!normalizarPosicion((state.data.props as Record<string, unknown>)?.[PROP_POSICION]),
    }));

    return (
      <div
        // Espejo del editor: allá `relative` hace falta para anclar los
        // controles, y acá mantiene idéntico el estilo computado.
        className="relative"
        ref={(node) => {
          if (node) {
            connectors.connect(node);
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }
        }}
        style={{
          padding: `${padding}px`,
          margin: `${margin}px 0`,
          background,
          borderRadius: '4px',
          border: selected ? '1px dashed #1e88e5' : '1px dashed transparent',
          ...enElLienzo(enLienzo),
        }}
      >
        <p
          style={{
            margin: 0,
            fontSize: `${fontSize}px`,
            fontWeight,
            textAlign,
            color,
          }}
          dangerouslySetInnerHTML={htmlConTipografiaMovil(htmlSeguro(text || ''))}
        />
      </div>
    );
  }
);

TextComponent.displayName = 'Text';

// Add craft property
(TextComponent as unknown as CraftComponent).craft = {
  displayName: 'Text',
  props: {
    text: 'Edit me',
    fontSize: 16,
    fontWeight: 'normal',
    textAlign: 'left',
    color: '#000000',
    background: 'transparent',
    padding: 10,
    margin: 5,
  },
};

export const Text = TextComponent as typeof TextComponent & CraftComponent;
